import React, { useState, useEffect } from 'react';
import { fetchTestimonials } from '../utils/api';
import { API_BASE_URL } from '../utils/api';

import q1 from '../../assets/testinomials/quote 1.svg';
import q2 from '../../assets/testinomials/quote 2.svg';
import fallbackImage from '../../assets/fallback.png';
import ExploreIcon from '../../assets/Shine.svg';

const MAX_CHAR_LENGTH = 120; // Maximum characters to show before truncation

const Testimonials = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedCards, setExpandedCards] = useState({}); // Tracks expanded status per card ID

  useEffect(() => {
    const loadTestimonials = async () => {
      try {
        setIsLoading(true);
        const data = await fetchTestimonials();
        const mappedTestimonials = data.map((item) => ({
          _id: item._id,
          img: item.image ? `${API_BASE_URL}/${item.image}` : fallbackImage,
          text: item.description,
          name: item.name,
          starRating: item.starRating,
        }));
        setTestimonials(mappedTestimonials);
      } catch (error) {
        console.error('Failed to load testimonials:', error);
      } finally {
        setIsLoading(false);
      }
    };
    loadTestimonials();
  }, []);

  const toggleExpand = (id) => {
    setExpandedCards((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  // Star rating component
  const StarRating = ({ rating }) => (
    <div className='flex items-center space-x-1'>
      {[...Array(5)].map((_, i) => (
        <svg
          key={i}
          className={`h-4 w-4 ${i < rating ? 'text-amber-500' : 'text-gray-200'}`}
          fill='currentColor'
          viewBox='0 0 20 20'
        >
          <path d='M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.97a1 1 0 00.95.69h4.18c.969 0 1.371 1.24.588 1.81l-3.39 2.46a1 1 0 00-.364 1.118l1.287 3.97c.3.921-.755 1.688-1.54 1.118l-3.39-2.46a1 1 0 00-1.175 0l-3.39 2.46c-.784.57-1.838-.197-1.54-1.118l1.287-3.97a1 1 0 00-.364-1.118L2.27 9.397c-.784-.57-.38-1.81.588-1.81h4.18a1 1 0 00.95-.69l1.286-3.97z' />
        </svg>
      ))}
    </div>
  );

  // Skeleton loader for a single card
  const SkeletonCard = () => (
    <div className='h-100 flex animate-pulse flex-col rounded-xl border border-gray-100 bg-white p-6 shadow-lg'>
      <div className='absolute right-6 top-4 text-5xl text-gray-200'>“</div>
      <div className='mt-6 flex-1'>
        <div className='space-y-3'>
          <div className='h-3 w-full rounded bg-gray-200'></div>
          <div className='h-3 w-11/12 rounded bg-gray-200'></div>
          <div className='h-3 w-10/12 rounded bg-gray-200'></div>
          <div className='h-3 w-3/4 rounded bg-gray-200'></div>
        </div>
      </div>
      <div className='mt-14 flex items-center justify-between gap-4 border-t border-gray-200 pt-4'>
        <div className='h-12 w-12 rounded-full bg-gray-200'></div>
        <div className='flex flex-col gap-2'>
          <div className='h-4 w-20 rounded bg-gray-200'></div>
          <div className='flex space-x-1'>
            {[...Array(5)].map((_, i) => (
              <div
                key={i}
                className='h-4 w-4 rounded bg-gray-200'
              ></div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  // Skeleton loader for grid
  const SkeletonLoader = () => (
    <div className='grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4'>
      {[...Array(4)].map((_, idx) => (
        <SkeletonCard key={idx} />
      ))}
    </div>
  );

  return (
    <>
      <div className='bg-gray-50 px-4 py-10 md:px-8 lg:px-16'>
        <div className=''>
          <div className='mb-8 flex flex-col items-center gap-3 md:mb-12'>
            {/* Small Heading with Icon */}
            <div className='flex items-center gap-3'>
              <img
                src={ExploreIcon}
                alt='explore'
                className='h-14 w-14'
              />
              <p className='mont text-2xl font-bold tracking-wide text-gray-900 md:text-3xl'>
                Happy Travel
              </p>
            </div>

            <div className='flex gap-3 text-center'>
              <h3 className='mont relative text-2xl font-bold text-gray-900 md:text-4xl'>
                Customer Stories
              </h3>
            </div>
          </div>
          {isLoading ? (
            <SkeletonLoader />
          ) : testimonials.length > 0 ? (
            <div className='grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4'>
              {testimonials.map((t) => {
                const isExpanded = !!expandedCards[t._id];
                const shouldTruncate = t.text && t.text.length > MAX_CHAR_LENGTH;

                return (
                  <div
                    key={t._id}
                    className='h-100 group relative flex flex-col rounded-lg bg-white p-6 shadow transition-all duration-500'
                  >
                    {/* Floating quote icon */}
                    <div className='absolute right-6 top-4 text-5xl text-gray-200 transition group-hover:text-gray-300'>
                      “
                    </div>

                    {/* Content */}
                    <div className='mt-6 flex-1'>
                      <p className='mb-3 leading-relaxed text-gray-700'>
                        {shouldTruncate && !isExpanded ? (
                          <>
                            {t.text.slice(0, MAX_CHAR_LENGTH)}...{' '}
                            <button
                              type='button'
                              onClick={() => toggleExpand(t._id)}
                              className='font-semibold text-blue-600 hover:underline'
                            >
                              Read more
                            </button>
                          </>
                        ) : (
                          <>
                            {t.text}{' '}
                            {shouldTruncate && (
                              <button
                                type='button'
                                onClick={() => toggleExpand(t._id)}
                                className='font-semibold text-blue-600 hover:underline'
                              >
                                Read less
                              </button>
                            )}
                          </>
                        )}
                      </p>
                    </div>

                    {/* Footer */}
                    <div className='mt-auto flex items-center justify-between gap-4 border-t border-gray-200 pt-4'>
                      <img
                        src={t.img}
                        alt={t.name}
                        className='h-12 w-12 rounded-full object-cover'
                        onError={(e) => (e.currentTarget.src = fallbackImage)}
                      />
                      <div className='flex flex-col'>
                        <h3 className='font-bold text-gray-700'>{t.name}</h3>
                        <StarRating rating={t.starRating} />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className='text-center text-lg text-gray-700'>
              No testimonials available at the moment.
            </p>
          )}
        </div>
      </div>
      <svg
        xmlns='http://www.w3.org/2000/svg'
        viewBox='0 0 1200 100'
        preserveAspectRatio='none'
        style={{
          backgroundColor: 'transparent',
          display: 'block',
          height: '30px',
          position: 'relative',
          top: '-1px',
          width: '100%',
        }}
      >
        <path
          d='M851.8,100c125,0,288.3-45,348.2-64V0H0v44c3.7-1,7.3-1.9,11-2.9C80.7,22,151.7,10.8,223.5,6.3C276.7,2.9,330,4,383,9.8 c52.2,5.7,103.3,16.2,153.4,32.8C623.9,71.3,726.8,100,851.8,100z'
          style={{ fill: '#f9fafb' }}
        />
      </svg>
    </>
  );
};

export default Testimonials;